import React, { useState, useRef } from 'react';
import { useUpload } from '../../hooks/useUpload';

const WORKER_API = process.env.REACT_APP_WORKER_API || 'http://localhost:8001';
const mono = { fontFamily: 'JetBrains Mono, monospace' };

// ── CICAPT-IIoT model metadata (single retrained thesis model) ───────────────
const APT_MODEL_META = {
  1: {
    label: 'CICAPT-IIoT Hybrid v2.1',
    desc:  'Dense AE + LightGBM retrained on Phase2 (no ports) — journal-grade methodology',
    acc:   '99.99%', f1: '84.47%', auc: '100%', mcc: '84.53%',
    precision: '81.31%', recall: '87.88%', fpr: '0.0022%', fnr: '12.12%',
    auc_pr: '91.94%',
    pipeline: 'RobustScaler → Dense AE (61→8→61) → LightGBM (62 features)',
    threshold: '0.9970  (best MCC on val set)',
    trained_on: 'CICAPT-IIoT Phase2  (60% train / 20% val / 20% test)',
    recommended: true,
  },
};

function isSameFileError(msg) {
  return /same.*file|already.*load|file.*match/i.test(msg);
}

export default function Upload({ onDetectionComplete, onDone }) {
  const _cb = onDetectionComplete || onDone;
  const { status, progress, result, preview, upload, detect } = useUpload();

  // ── CICAPT-IIoT is the only detection mode (APT-only scope) ──────────────
  const [datasetMode, setDatasetMode] = useState('apt');
  const isAPT = datasetMode === 'apt';

  const MODEL_META     = APT_MODEL_META;
  const defaultModelId = 1;

  const [selectedModel,   setSelectedModel]   = useState(1);
  const [activeModel,     setActiveModel]      = useState(null);
  const [switching,       setSwitching]        = useState(false);
  const [switchMsg,       setSwitchMsg]        = useState('');
  const [switchMsgType,   setSwitchMsgType]    = useState('success');
  const fileRef = useRef();

  // Reset selection when toggling dataset
  const handleModeToggle = (mode) => {
    setDatasetMode(mode);
    setSelectedModel(1);
    setActiveModel(null);
    setSwitchMsg('');
  };

  const handleFile = (file) => {
    if (file && file.name.endsWith('.csv')) upload(file);
  };

  const handleModelSelect = (mid) => setSelectedModel(mid);

  const handleDetect = async () => {
    setSwitching(true);

    // Single model (CICAPT-IIoT hybrid) is always loaded — no switch needed.
    setActiveModel(selectedModel);
    setSwitchMsg('\u2713 ' + (MODEL_META[selectedModel] ? MODEL_META[selectedModel].label : 'CICAPT-IIoT') + ' active \u2014 running detection\u2026');
    setSwitchMsgType('success');
    setSwitching(false);
    detect(_cb);
  };

  const meta     = MODEL_META[selectedModel] || MODEL_META[defaultModelId];
  const msgColor = switchMsgType === 'warning' ? '#f59e0b' : '#10b981';

  // ── Accent colours ────────────────────────────────────────────────────────
  const accent       = '#f59e0b';
  const accentSoft   = '#f59e0b22';
  const accentBorder = '#f59e0b44';
  const modeLabel    = 'CICAPT-IIoT APT Detection';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

      {/* ── LEFT COLUMN ── */}
      <div>

        {/* ── Dataset mode toggle ── */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 8 }}>DETECTION MODE</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { key: 'apt', label: '🟡  CICAPT-IIoT',    sub: 'APT attack detection (IIoT)' },
              
            ].map(({ key, label, sub }) => (
              <div
                key={key}
                onClick={() => handleModeToggle(key)}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 7, cursor: 'pointer',
                  border: `1px solid ${datasetMode === key ? accent : '#ffffff14'}`,
                  background: datasetMode === key ? accentSoft : '#141720',
                  transition: '.15s',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: datasetMode === key ? accent : '#9ca3af', ...mono }}>
                  {datasetMode === key ? '● ' : '○ '}{label}
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginTop: 2 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Upload area ── */}
        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 8 }}>DATASET UPLOAD</div>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); }}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          style={{
            border: `2px dashed ${accentBorder}`,
            borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer',
            background: '#141720', transition: '.2s',
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#e8eaf0', marginBottom: 5 }}>Drop CSV or click to browse</div>
          <div style={{ fontSize: 11, color: '#6b7280', ...mono }}>
            {status === 'ready'
              ? `✓ ${preview?.filename} — ${preview?.rows?.toLocaleString()} rows ready`
              : 'Supports CICAPT-IIoT Phase2 format  ·  cicapt_sample_test.csv'}
          </div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
        </div>

        {/* ── Sample file hint for APT mode ── */}
        {isAPT && (
          <div style={{ marginTop: 6, fontSize: 10, color: '#6b7280', ...mono, padding: '5px 8px', background: '#ffffff05', borderRadius: 4 }}>
            💡 Sample test file available at <span style={{ color: accent }}>/cicapt_sample_test.csv</span> — 4,300 rows with 300 real APT attacks
          </div>
        )}

        {/* ── Model Selector ── */}
        <div style={{ marginTop: 12, background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 10 }}>
            MODEL SELECTOR — {modeLabel.toUpperCase()}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {Object.entries(MODEL_META).map(([id, m]) => {
              const mid      = parseInt(id);
              const selected = mid === selectedModel;
              const isActive = activeModel !== null && mid === activeModel;
              return (
                <div
                  key={id}
                  onClick={() => handleModelSelect(mid)}
                  style={{
                    padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${selected ? accent : '#ffffff14'}`,
                    background: selected ? accentSoft : '#0d0f14',
                    transition: '.15s', position: 'relative',
                  }}
                >
                  <div style={{ position: 'absolute', top: 6, right: 8, display: 'flex', gap: 4 }}>
                    {isActive && (
                      <span style={{ fontSize: 9, color: '#60a5fa', ...mono, background: '#3b82f618', padding: '1px 5px', borderRadius: 3, border: '1px solid #3b82f633' }}>ACTIVE</span>
                    )}
                    {m.recommended && (
                      <span style={{ fontSize: 9, color: '#10b981', ...mono, background: '#10b98118', padding: '1px 5px', borderRadius: 3, border: '1px solid #10b98133' }}>REC</span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 2 }}>MODEL {id}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: selected ? accent : '#e8eaf0', ...mono, marginBottom: 3 }}>
                    {selected ? '● ' : '○ '}{m.label}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 6 }}>{m.desc}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[['ACC', m.acc, '#3b82f6'], ['F1', m.f1, '#10b981'], ['AUC', m.auc, '#8b5cf6'], ['MCC', m.mcc, '#f59e0b']].map(([k, v, c]) => (
                      <div key={k} style={{ fontSize: 9, ...mono }}>
                        <span style={{ color: '#6b7280' }}>{k} </span>
                        <span style={{ color: c, fontWeight: 700 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  {/* APT model: show extra pipeline info */}
                  {isAPT && m.pipeline && (
                    <div style={{ marginTop: 8, padding: '6px 8px', background: '#ffffff05', borderRadius: 4, fontSize: 9, color: '#9ca3af', ...mono }}>
                      <div>🔗 Pipeline: {m.pipeline}</div>
                      <div style={{ marginTop: 3 }}>⚡ Threshold: {m.threshold}</div>
                      <div style={{ marginTop: 3 }}>📊 Trained: {m.trained_on}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Run button ── */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={handleDetect}
            disabled={!['ready', 'complete'].includes(status) || switching}
            style={{
              flex: 1, padding: 10, borderRadius: 5, fontSize: 12, fontWeight: 500,
              cursor: 'pointer', border: `1px solid ${accentBorder}`,
              background: accentSoft, color: accent, ...mono,
              opacity: (!['ready', 'complete'].includes(status) || switching) ? .4 : 1,
            }}
          >
            {switching ? '⟳ Loading model…' : `▶ Run Detection — ${meta.label}`}
          </button>
        </div>

        {switchMsg && (
          <div style={{ marginTop: 8, fontSize: 11, color: msgColor, ...mono, padding: '6px 10px', background: '#ffffff08', borderRadius: 5 }}>
            {switchMsg}
          </div>
        )}

        {/* ── Progress ── */}
        {progress && (
          <div style={{ marginTop: 14, background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 8 }}>DETECTION PROGRESS</div>
            {Array.from({ length: progress.total }, (_, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12, ...mono, color: i <= progress.step ? '#34d399' : '#6b7280' }}>
                <span>{i < progress.step ? '✓' : i === progress.step ? '▶' : '○'}</span>
                <span>{i === progress.step ? progress.label : i < progress.step ? `Step ${i + 1} complete` : `Step ${i + 1} pending`}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Result ── */}
        {result && (
          <div style={{ marginTop: 12, background: '#10b98118', border: '1px solid #10b98144', borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, color: '#34d399', ...mono, marginBottom: 6 }}>
              ✓ DETECTION COMPLETE — {MODEL_META[activeModel ?? selectedModel]?.label}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, ...mono, color: '#9ca3af' }}>
              <div>Total: <span style={{ color: '#e8eaf0' }}>{result.total_samples?.toLocaleString()}</span></div>
              <div>Attacks: <span style={{ color: '#ff6b7a' }}>{result.attacks_detected?.toLocaleString()}</span></div>
              <div>Normal: <span style={{ color: '#34d399' }}>{result.normal_traffic?.toLocaleString()}</span></div>
              <div>Incidents: <span style={{ color: '#60a5fa' }}>{result.incidents_clustered}</span></div>
              <div>HIGH: <span style={{ color: '#ff3b4e' }}>{result.severity_distribution?.HIGH}</span></div>
              <div>MEDIUM: <span style={{ color: '#f59e0b' }}>{result.severity_distribution?.MEDIUM}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* ── RIGHT COLUMN ── */}
      <div>
        <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 10 }}>DATASET PREVIEW</div>
        <div style={{ background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: 14, minHeight: 200, overflowX: 'auto' }}>
          {preview?.preview ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, ...mono }}>
              <thead>
                <tr>{Object.keys(preview.preview[0]).slice(0, 6).map(k => (
                  <th key={k} style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #ffffff14' }}>{k}</th>
                ))}</tr>
              </thead>
              <tbody>
                {preview.preview.map((row, i) => (
                  <tr key={i}>
                    {Object.values(row).slice(0, 6).map((v, j) => (
                      <td key={j} style={{ padding: '4px 8px', color: '#9ca3af', borderBottom: '1px solid #ffffff0a' }}>
                        {typeof v === 'number' ? v.toFixed(2) : String(v).slice(0, 20)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 40, fontSize: 13 }}>
              No data loaded.<br />Upload a CSV to preview.
            </div>
          )}
        </div>

        {/* ── Model performance panel ── */}
        <div style={{ marginTop: 12, background: '#141720', border: `1px solid ${accentBorder}`, borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em', marginBottom: 2 }}>
            MODEL PERFORMANCE — TRAINED: CICAPT-IIoT Phase2 · EVALUATED: held-out test set (20%)
          </div>
          <div style={{ fontSize: 12, color: accent, ...mono, marginTop: 6, marginBottom: 10 }}>
            MODEL {selectedModel} — {meta.label}
          </div>

          {/* 6-metric grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              ['ACCURACY',  meta.acc,       '#3b82f6'],
              ['F1-SCORE',  meta.f1,        '#10b981'],
              ['PRECISION', meta.precision, '#f59e0b'],
              ['RECALL',    meta.recall,    '#34d399'],
              ['AUC-ROC',   meta.auc,       '#8b5cf6'],
              ['MCC',       meta.mcc,       '#ff6b7a'],
            ].map(([k, v, c]) => (
              <div key={k} style={{ background: '#0d0f14', borderRadius: 5, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: c, ...mono }}>{v}</div>
                <div style={{ fontSize: 9, color: '#6b7280', ...mono, marginTop: 3 }}>{k}</div>
              </div>
            ))}
          </div>

          {/* AUC-PR for APT model */}
          {isAPT && meta.auc_pr && (
            <div style={{ marginTop: 8, background: '#0d0f14', borderRadius: 5, padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#f472b6', ...mono }}>{meta.auc_pr}</div>
              <div style={{ fontSize: 9, color: '#6b7280', ...mono, marginTop: 3 }}>AUC-PR  (primary metric for imbalanced data)</div>
            </div>
          )}

          {/* FPR / FNR */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
            <div style={{ background: '#0d0f14', borderRadius: 5, padding: '10px 14px' }}>
              <div style={{ fontSize: 10, color: '#9ca3af', ...mono, marginBottom: 2 }}>FALSE POSITIVE RATE</div>
              <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 6 }}>Normal flagged as attack</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b', ...mono }}>{meta.fpr}</div>
            </div>
            <div style={{ background: '#0d0f14', borderRadius: 5, padding: '10px 14px' }}>
              <div style={{ fontSize: 10, color: '#9ca3af', ...mono, marginBottom: 2 }}>FALSE NEGATIVE RATE</div>
              <div style={{ fontSize: 10, color: '#6b7280', ...mono, marginBottom: 6 }}>Attacks missed (low FNR)</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981', ...mono }}>{meta.fnr}</div>
            </div>
          </div>

          {/* APT model: threshold + imbalance note */}
          {isAPT && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#ffffff05', borderRadius: 5, fontSize: 10, color: '#9ca3af', ...mono }}>
              <div>⚡ <span style={{ color: accent }}>Threshold:</span> {meta.threshold}</div>
              <div style={{ marginTop: 4 }}>📊 <span style={{ color: accent }}>Class imbalance:</span> 9,498:1  (benign:attack) — undersampled 50:1 for training</div>
              
            </div>
          )}
        </div>
      </div>

    </div>
  );
}